import { useMemo, useSyncExternalStore, type ComponentProps } from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import i18n from '@/i18n/config';
import { getClerkLocalization } from '@/lib/clerk-localization';
import {
  buildClerkAppearance,
  readDesignTokens,
  type DesignTokens,
} from '@/lib/clerk-theme';
import { subscribeTheme } from '@/lib/theme-subscribe';

/**
 * Wraps ClerkProvider so its `localization` and `appearance` props track the
 * app — the language from i18next, the palette from the live design tokens.
 *
 * Clerk's prebuilt components default to English; without this they'd stay
 * English even after i18next detected (or the user switched to) Ukrainian.
 * We subscribe to i18next's `languageChanged` event via `useSyncExternalStore`
 * — the idiomatic way to read an external store — so toggling the language
 * anywhere in the app live-updates Clerk's UI too, without re-rendering on
 * unrelated state.
 *
 * Тема читается отсюда же и по той же схеме, но из DOM: `ClerkProvider`
 * монтируется ВНЕ `ThemeProvider` (см. main.tsx), поэтому контекста темы у
 * него нет, а класс `light`/`dark` на `documentElement` есть всегда — его
 * ставит до-гидрационный скрипт в index.html и поддерживает `ThemeProvider`,
 * включая режим Auto. Без этого Clerk рисовал тёмную карточку на светлой
 * странице.
 */
function subscribeLanguage(onChange: () => void): () => void {
  i18n.on('languageChanged', onChange);
  return () => i18n.off('languageChanged', onChange);
}

function getLanguageSnapshot(): string {
  return i18n.language || 'en';
}

let tokensCache: { key: string; tokens: DesignTokens } | null = null;

/**
 * Дешёвый ключ темы: два атрибута, которыми палитра выбирается, плюс ОДНА
 * проба самого цвета.
 *
 * Проба здесь не для красоты. Без неё ключ не менялся бы, пока таблица стилей
 * доезжает: в дев-режиме Vite подкладывает CSS отдельным модулем уже после
 * первого рендера, и снимок навсегда застревал бы на запасных значениях —
 * Clerk весь сеанс красился бы цветами сплеша. Одно чтение против семнадцати,
 * и момент появления палитры при этом не пропущен.
 */
function themeKey(): string {
  if (typeof document === 'undefined') return '';
  const root = document.documentElement;
  const probe =
    typeof getComputedStyle === 'function'
      ? getComputedStyle(root).getPropertyValue('--color-fg').trim()
      : '';
  return `${root.className}|${root.getAttribute('data-color-scheme') ?? ''}|${probe}`;
}

function getTokensSnapshot(): DesignTokens {
  // Сначала дешёвый ключ, и только на его смене — чтение всей палитры: иначе
  // каждый рендер корневого провайдера стоил бы семнадцати `getPropertyValue`.
  const key = themeKey();
  if (!tokensCache || tokensCache.key !== key) {
    tokensCache = { key, tokens: readDesignTokens() };
  }
  // Один и тот же объект, пока тема не сменилась: `useSyncExternalStore`
  // сравнивает снимки по `Object.is`, и новый объект каждый раз читался бы как
  // смена темы.
  return tokensCache.tokens;
}

/**
 * Пропсы ClerkProvider — объединение (ключ публикации ИЛИ готовый экземпляр
 * Clerk), поэтому `Omit` применяется к каждому члену по отдельности: обычный
 * `Omit` схлопнул бы объединение в один тип и перестал бы подходить вызову.
 */
type WithoutOverridden<T> = T extends unknown
  ? Omit<T, 'appearance' | 'localization'>
  : never;

export function LocalizedClerkProvider(
  // `appearance` и `localization` исключены из типа намеренно: оба
  // собираются здесь и перебили бы переданное снаружи молча — тип обещал бы
  // приём, а код игнорировал. Пусть это будет ошибкой компиляции, а не
  // сюрпризом.
  props: WithoutOverridden<ComponentProps<typeof ClerkProvider>>
) {
  const language = useSyncExternalStore(
    subscribeLanguage,
    getLanguageSnapshot,
    getLanguageSnapshot
  );
  const tokens = useSyncExternalStore(
    subscribeTheme,
    getTokensSnapshot,
    getTokensSnapshot
  );

  const appearance = useMemo(() => buildClerkAppearance(tokens), [tokens]);

  return (
    <ClerkProvider
      {...props}
      appearance={appearance}
      localization={getClerkLocalization(language)}
    />
  );
}
